'use client'

import { usePathname } from 'next/navigation'
import { memo, useCallback, useEffect, useState } from 'react'

import { GroupedNav } from './GroupedNav'
import { maxWidthLayoutCSS } from './PageTemplate'
import { TopNavUserMenu } from './TopNavUserMenu'
import { NotificationBell } from '@components/NotificationBell'
import { APP_HOTKEYS_LS_KEY, isTypingTarget } from '@features/appHotkeys/appHotkeys'
import { MockModeControl } from '@features/mockMode/MockModeControl'
import { PrimeModeToggle } from '@features/primeMode/PrimeModeToggle'
import { PRIME_TOP_NAV_CLASS, PRIME_TOP_NAV_PATTERN_CLASS } from '@features/primeMode/primeStyles'
import { VoisoNavButton } from '@features/voiso/VoisoNavButton'
import { useLocalStorage } from '@hooks/useLocalStorage'
import { usePrimeMode } from '@hooks/usePrimeMode'
import { cn } from '@lib/cn'
import { VOISO_EMBED_URL } from '@lib/constants'
import { Logo } from '@ui/Logo'

export const TopNav = memo(() => {
  const [isCtrlKeyDown, setIsCtrlKeyDown] = useState(false)
  const pathname = usePathname()

  const { valueLS: isAppHotkeysEnabled, setValueLS: setIsAppHotkeysEnabled } = useLocalStorage(
    APP_HOTKEYS_LS_KEY,
    false,
  )

  const isPrimeMode = usePrimeMode()

  const handleAppHotkeysToggle = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault()

      setIsAppHotkeysEnabled(!isAppHotkeysEnabled)
    },
    [setIsAppHotkeysEnabled, isAppHotkeysEnabled],
  )

  useEffect(() => {
    const syncFromEvent = (e: KeyboardEvent) => setIsCtrlKeyDown(e.ctrlKey)
    const clear = () => setIsCtrlKeyDown(false)

    const handleKeyDown = (e: KeyboardEvent) => {
      if (isTypingTarget(e.target)) return

      syncFromEvent(e)
    }

    // Release and blur are unguarded: skipping them for typing targets leaves the hints stuck on.
    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('keyup', syncFromEvent)
    window.addEventListener('blur', clear)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('keyup', syncFromEvent)
      window.removeEventListener('blur', clear)
    }
  }, [])

  const showHotkeys = isAppHotkeysEnabled && isCtrlKeyDown

  return (
    <div
      className={cn(
        'fixed top-0 left-0 right-0 z-40 border-b backdrop-blur-lg transition-colors',
        isPrimeMode ? PRIME_TOP_NAV_CLASS : 'bg-card/20 border-input',
      )}
      data-prime-mode={isPrimeMode || undefined}
    >
      <MockModeControl />
      {isPrimeMode && (
        <div aria-hidden className={cn('absolute inset-0 pointer-events-none', PRIME_TOP_NAV_PATTERN_CLASS)} />
      )}
      <div
        className={cn('relative flex items-center justify-between px-5 h-20 w-full animate-fade-in', maxWidthLayoutCSS)}
      >
        <Logo />

        <GroupedNav pathname={pathname} showHotkeys={showHotkeys} />

        <div className='flex items-center gap-0 ml-auto'>
          <PrimeModeToggle />
          {VOISO_EMBED_URL && <VoisoNavButton />}
          <NotificationBell />
          <TopNavUserMenu isAppHotkeysEnabled={isAppHotkeysEnabled} onToggleAppHotkeys={handleAppHotkeysToggle} />
        </div>
      </div>
    </div>
  )
})
